import { useCallback, useEffect, useState } from "react";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { Box, Button, Typography } from "@mui/material";
import { useAuditDashboardVideoMutation } from "../../../hooks";
import { auditDashboardTheme } from "../../../theme";

interface ShiftVideoSectionProps {
  auditId?: string | null;
  enabled: boolean;
  hasVideo: boolean;
}

export default function ShiftVideoSection({ auditId, enabled, hasVideo }: ShiftVideoSectionProps) {
  const [openVideo, setOpenVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const fetchVideoMutation = useAuditDashboardVideoMutation();

  useEffect(() => {
    setOpenVideo(false);
    setVideoUrl(null);
    setLoadError(null);
  }, [auditId]);

  const handleOpenVideo = useCallback(async () => {
    if (!auditId) {
      return;
    }

    setLoadError(null);
    const playbackUrl = await fetchVideoMutation.mutateAsync(auditId);
    if (playbackUrl) {
      setVideoUrl(playbackUrl);
      setOpenVideo(true);
      return;
    }

    setLoadError("Unable to load video.");
  }, [auditId, fetchVideoMutation]);

  const handleCloseVideo = useCallback(() => {
    setOpenVideo(false);
    setVideoUrl(null);
  }, []);

  if (!enabled || !auditId || !hasVideo) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          px: 1.5,
          pt: 1.5,
          pb: 1.5,
          bgcolor: "background.paper",
          backgroundImage: "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            p: 1.5,
            border: `1px solid ${auditDashboardTheme.colors.lineStrong}`,
            borderRadius: "8px",
            bgcolor: auditDashboardTheme.colors.surface,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2, color: auditDashboardTheme.colors.text }}>
              Audit Video
            </Typography>
            <Typography sx={{ mt: 0.35, fontSize: 12, fontWeight: 500, lineHeight: 1.3, color: auditDashboardTheme.colors.textMutedStrong }}>
              Open the recorded video for this audit shift.
            </Typography>
            {loadError ? (
              <Typography sx={{ mt: 0.5, fontSize: 12, fontWeight: 500, lineHeight: 1.2, color: auditDashboardTheme.colors.textNegative }}>
                {loadError}
              </Typography>
            ) : null}
          </Box>

          <Button
            variant="outlined"
            onClick={openVideo ? handleCloseVideo : handleOpenVideo}
            disabled={fetchVideoMutation.isPending}
            startIcon={<PlayCircleOutlineIcon fontSize="small" />}
            sx={{
              flexShrink: 0,
              minHeight: 34,
              px: 1.75,
              borderColor: auditDashboardTheme.colors.blue,
              color: auditDashboardTheme.colors.blue,
              textTransform: "none",
              fontSize: 13,
              fontWeight: 700,
              whiteSpace: "nowrap",
            }}
          >
            {fetchVideoMutation.isPending ? "Loading..." : openVideo ? "Hide Video" : "View Video"}
          </Button>
        </Box>

        {openVideo && videoUrl ? (
          <Box
            sx={{
              mt: 1.5,
              mx: "auto",
              width: "100%",
              maxWidth: { xs: 320, sm: 380, md: 430 },
              p: 1.5,
              border: `1px solid ${auditDashboardTheme.colors.lineStrong}`,
              borderRadius: "8px",
              bgcolor: "#0b1220",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Box
                component="video"
                src={videoUrl}
                controls
                autoPlay
                preload="metadata"
                sx={{
                  display: "block",
                  width: "100%",
                  maxWidth: { xs: 280, sm: 340, md: 380 },
                  maxHeight: "76vh",
                  borderRadius: "10px",
                  bgcolor: "#000000",
                }}
              />
            </Box>
          </Box>
        ) : null}
      </Box>
    </>
  );
}
